import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { CircleCheck, TriangleAlert } from 'lucide-react';
import { useDispatch } from 'react-redux';

import { validateRoutingNumberAction } from 'actions/workflow';
import { Input } from 'components/Form';
import Spinner from 'components/Spinner';
import { routingNumberMask } from 'utils/masks';

const INITIAL_VALIDATION_STATE = {
  loading: false,
  verified: false,
  checkedFirstTime: false,
  data: null,
};

const RoutingNumberInput = ({
  value,
  error,
  bankName,
  onChange,
  onValidationSuccess,
  onValidationError,
  required = true,
  className = '',
}) => {
  const dispatch = useDispatch();
  const [validationStatus, setValidationStatus] = useState(INITIAL_VALIDATION_STATE);

  const validateRoutingNo = (routingNumber) => {
    const newValue = routingNumber.replace(/[^\d]/g, '');
    if (newValue.length === 9) {
      return true;
    } else if (validationStatus.checkedFirstTime) {
      setValidationStatus(prev => ({ ...prev, verified: false }));
    }
    return false;
  };

  const handleChange = (event) => {
    if (validationStatus.loading) return;
    
    const newValue = event.target.value;
    onChange(newValue);

    if (validateRoutingNo(newValue) && newValue !== value) {
      setValidationStatus(prev => ({ ...prev, checkedFirstTime: true, loading: true, data: null }));
      
      dispatch(validateRoutingNumberAction({
        routingNumber: newValue,
        success: (res) => {
          const validateResponse = res && res.length > 0 ? res[0] : null;
          const validateStatus = validateResponse && validateResponse.status;
          const validateData = validateResponse && validateResponse.data;

          if (validateStatus === 'success') {
            setValidationStatus(prev => ({ ...prev, checkedFirstTime: true, verified: true, loading: false, data: validateData }));
            onValidationSuccess(validateData);
          }
          if (validateStatus === 'fail') {
            setValidationStatus(prev => ({ ...prev, checkedFirstTime: true, verified: false, loading: false, data: null }));
            onValidationError(validateData.message || 'An error occurred', newValue);
          }
        },
        fail: () => {
          setValidationStatus(prev => ({ ...prev, checkedFirstTime: true, verified: false, loading: false, data: null }));
          onValidationError('Validation failed', newValue);
        },
      }));
    }
  };

  return (
    <div className={`relative ${className}`}>
      <Input
        required={required}
        className="font-numeric"
        label="Routing Number"
        inputMode="numeric"
        value={value}
        error={error}
        onChange={handleChange}
        mask={routingNumberMask}
      />
      {bankName && validationStatus.verified ? (
        <span className="px-2 text-blue-400 text-xs">
          {bankName}
        </span>
      ) : null}
      {validationStatus.loading ? <Spinner className="absolute right-5 top-10" size={4} color="blue-400" /> : null}
      {!validationStatus.loading && validationStatus.checkedFirstTime ? (
        <span className="absolute right-4 top-9">
          {validationStatus.verified ? <CircleCheck className="text-green-500" /> : <TriangleAlert className="text-red-500" />}
        </span>
      ) : null}
    </div>
  );
};

RoutingNumberInput.propTypes = {
  value: PropTypes.string.isRequired,
  error: PropTypes.string,
  bankName: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  onValidationSuccess: PropTypes.func.isRequired,
  onValidationError: PropTypes.func.isRequired,
  required: PropTypes.bool,
  className: PropTypes.string,
};

export default RoutingNumberInput;
